import React from 'react';
import { Heart, MessageCircle, Share2, Bookmark, MoreHorizontal } from 'lucide-react';
import { PLATFORMS, PlatformKey } from '../config/platformConfig';
import { PlatformContent } from '../types/socialMedia.types';

interface PostPreviewProps {
  platform: PlatformKey;
  content: PlatformContent[PlatformKey];
}

export default function PostPreview({ platform, content }: PostPreviewProps) {
  const platformConfig = PLATFORMS[platform];

  const renderPlatformSpecificPreview = () => {
    switch (platform) {
      case 'facebook':
        return <FacebookPreview content={content as any} />;
      case 'instagram':
        return <InstagramPreview content={content as any} />;
      case 'linkedin':
        return <LinkedInPreview content={content as any} />;
      case 'twitter':
        return <TwitterPreview content={content as any} />;
      case 'youtube':
        return <YouTubePreview content={content as any} />;
      case 'googlebusiness':
        return <GoogleBusinessPreview content={content as any} />;
      default:
        return null;
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
      {/* Preview Header */}
      <div className="px-4 py-3 bg-gray-50 border-b flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-700">Live Preview</h3>
        <img
          src={platformConfig.icon}
          alt={platformConfig.name}
          className="w-5 h-5"
        />
      </div>

      {/* Platform-specific preview */}
      <div className="p-4">
        {renderPlatformSpecificPreview()}
      </div>
    </div>
  );
}

// Facebook Preview
function FacebookPreview({ content }: { content: any }) {
  return (
    <div className="space-y-3">
      {/* Profile Header */}
      <div className="flex items-center space-x-3">
        <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full"></div>
        <div className="flex-1">
          <p className="font-semibold text-sm">Your Page</p>
          <p className="text-xs text-gray-500">Just now · 🌍</p>
        </div>
        <MoreHorizontal className="w-5 h-5 text-gray-400" />
      </div>

      {/* Post Content */}
      <div
        className="text-sm text-gray-900"
        dangerouslySetInnerHTML={{
          __html: content.text || '<span class="text-gray-400">No content</span>'
        }}
      />

      {/* Media Preview */}
      {content.media && content.media.length > 0 && (
        <div className={`grid gap-1 ${
          content.media.length === 1 ? 'grid-cols-1' :
          content.media.length === 2 ? 'grid-cols-2' :
          content.media.length === 3 ? 'grid-cols-2' :
          'grid-cols-2'
        }`}>
          {content.media.slice(0, 4).map((media: any, idx: number) => (
            <div
              key={idx}
              className={`relative bg-gray-200 ${
                content.media.length === 3 && idx === 0 ? 'row-span-2' : ''
              } ${content.media.length === 1 ? 'aspect-video' : 'aspect-square'} overflow-hidden rounded`}
            >
              {media.type === 'video' ? (
                <div className="flex items-center justify-center h-full bg-black">
                  <span className="text-white text-3xl">▶️</span>
                </div>
              ) : (
                <img
                  src={media.url}
                  alt={media.altText || ''}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.currentTarget.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"%3E%3Crect fill="%23ddd" width="24" height="24"/%3E%3C/svg%3E';
                  }}
                />
              )}
              {content.media.length > 4 && idx === 3 && (
                <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                  <span className="text-white text-2xl font-bold">+{content.media.length - 4}</span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Link Preview */}
      {content.link && (
        <div className="border rounded-lg overflow-hidden">
          <div className="h-40 bg-gray-200"></div>
          <div className="p-3">
            <p className="text-xs text-gray-500 uppercase">website.com</p>
            <p className="text-sm font-semibold">Link Preview Title</p>
            <p className="text-xs text-gray-600">Link description...</p>
          </div>
        </div>
      )}

      {/* Engagement Bar */}
      <div className="flex items-center justify-between pt-2 border-t text-gray-600">
        <button className="flex items-center space-x-2 hover:text-blue-600">
          <Heart className="w-4 h-4" />
          <span className="text-xs">Like</span>
        </button>
        <button className="flex items-center space-x-2 hover:text-blue-600">
          <MessageCircle className="w-4 h-4" />
          <span className="text-xs">Comment</span>
        </button>
        <button className="flex items-center space-x-2 hover:text-blue-600">
          <Share2 className="w-4 h-4" />
          <span className="text-xs">Share</span>
        </button>
      </div>
    </div>
  );
}

// Instagram Preview
function InstagramPreview({ content }: { content: any }) {
  return (
    <div className="space-y-3">
      {/* Profile Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full"></div>
          <div>
            <p className="font-semibold text-sm">yourbrand</p>
            {content.location && (
              <p className="text-xs text-gray-500">{content.location}</p>
            )}
          </div>
        </div>
        <MoreHorizontal className="w-5 h-5" />
      </div>

      {/* Media Preview */}
      {content.media && content.media.length > 0 ? (
        <div className="relative">
          {content.media.length === 1 ? (
            <div className="aspect-square bg-gray-200 rounded overflow-hidden">
              {content.media[0].type === 'video' ? (
                <div className="flex items-center justify-center h-full bg-black">
                  <span className="text-white text-4xl">▶️</span>
                </div>
              ) : (
                <img
                  src={content.media[0].url}
                  alt={content.media[0].altText || ''}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.currentTarget.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"%3E%3Crect fill="%23f0f0f0" width="24" height="24"/%3E%3C/svg%3E';
                  }}
                />
              )}
            </div>
          ) : (
            <div className="relative aspect-square bg-gray-200 rounded overflow-hidden">
              <img
                src={content.media[0].url}
                alt={content.media[0].altText || ''}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.currentTarget.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"%3E%3Crect fill="%23f0f0f0" width="24" height="24"/%3E%3C/svg%3E';
                }}
              />
              <div className="absolute top-2 right-2 bg-white bg-opacity-90 px-2 py-1 rounded text-xs font-semibold">
                1/{content.media.length}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="aspect-square bg-gradient-to-br from-pink-100 to-purple-100 rounded"></div>
      )}

      {/* Engagement Icons */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Heart className="w-6 h-6 hover:text-red-500 cursor-pointer" />
          <MessageCircle className="w-6 h-6 hover:text-gray-700 cursor-pointer" />
          <Share2 className="w-6 h-6 hover:text-gray-700 cursor-pointer" />
        </div>
        <Bookmark className="w-6 h-6 hover:text-gray-700 cursor-pointer" />
      </div>

      {/* Caption */}
      <div className="space-y-1">
        <p className="text-sm">
          <span className="font-semibold mr-2">yourbrand</span>
          <span dangerouslySetInnerHTML={{
            __html: content.text || 'Caption text...'
          }} />
        </p>
        {content.hashtags && (
          <p className="text-sm text-blue-600">{content.hashtags}</p>
        )}
      </div>
    </div>
  );
}

// LinkedIn Preview
function LinkedInPreview({ content }: { content: any }) {
  return (
    <div className="space-y-3">
      {/* Profile Header */}
      <div className="flex items-start space-x-3">
        <div className="w-12 h-12 bg-blue-600 rounded"></div>
        <div className="flex-1">
          <p className="font-semibold text-sm">Your Company</p>
          <p className="text-xs text-gray-500">10,000 followers</p>
          <p className="text-xs text-gray-500">Just now · {content.visibility || 'Public'}</p>
        </div>
      </div>

      {/* Post Content */}
      {content.postType === 'Article' && content.headline && (
        <h3 className="text-lg font-bold">{content.headline}</h3>
      )}

      <div
        className="text-sm text-gray-900"
        dangerouslySetInnerHTML={{
          __html: content.text || '<span class="text-gray-400">No content</span>'
        }}
      />

      {/* Engagement Stats */}
      <div className="flex items-center text-xs text-gray-500 space-x-4">
        <span>👍 12</span>
        <span>2 comments</span>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-around pt-2 border-t text-gray-600 text-sm">
        <button className="hover:text-blue-600">👍 Like</button>
        <button className="hover:text-blue-600">💬 Comment</button>
        <button className="hover:text-blue-600">🔄 Repost</button>
        <button className="hover:text-blue-600">📤 Send</button>
      </div>
    </div>
  );
}

// Twitter/X Preview
function TwitterPreview({ content }: { content: any }) {
  const tweetText = content.text || '';
  const isOverLimit = tweetText.length > 280;

  return (
    <div className="space-y-3">
      {/* Profile Header */}
      <div className="flex items-start space-x-3">
        <div className="w-10 h-10 bg-black rounded-full"></div>
        <div className="flex-1">
          <div className="flex items-center space-x-1">
            <p className="font-bold text-sm">Your Brand</p>
            <span className="text-blue-500">✓</span>
            <span className="text-gray-500 text-sm">@yourbrand · now</span>
          </div>

          {/* Tweet Text */}
          <div className="mt-2">
            <p className={`text-sm ${isOverLimit ? 'text-red-600' : 'text-gray-900'}`}>
              {tweetText || 'Tweet text...'}
            </p>
            {content.hashtags && (
              <p className="text-sm text-blue-500 mt-1">{content.hashtags}</p>
            )}
          </div>

          {/* Thread Preview */}
          {content.postType === 'Thread' && content.threadTexts?.length > 0 && (
            <div className="mt-3 border-l-2 border-gray-300 pl-3 space-y-2">
              {content.threadTexts.slice(0, 2).map((tweet: string, i: number) => (
                <div key={i} className="text-sm text-gray-700">
                  <p className="text-xs text-gray-500">Thread {i + 2}/{content.threadTexts.length + 1}</p>
                  <p>{tweet}</p>
                </div>
              ))}
              {content.threadTexts.length > 2 && (
                <p className="text-xs text-blue-500">+{content.threadTexts.length - 2} more tweets</p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between text-gray-500 text-sm">
        <button className="hover:text-blue-500">💬</button>
        <button className="hover:text-green-500">🔄</button>
        <button className="hover:text-red-500">♥️</button>
        <button className="hover:text-blue-500">📊</button>
        <button className="hover:text-blue-500">🔗</button>
      </div>
    </div>
  );
}

// YouTube Preview
function YouTubePreview({ content }: { content: any }) {
  return (
    <div className="space-y-3">
      {/* Video Thumbnail */}
      <div className="relative aspect-video bg-black rounded-lg overflow-hidden">
        {content.thumbnail ? (
          <img
            src={content.thumbnail}
            alt="Video thumbnail"
            className="w-full h-full object-cover"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
        ) : (
          <div className="flex items-center justify-center h-full">
            <div className="text-white text-4xl">▶️</div>
          </div>
        )}
        <div className="absolute bottom-2 right-2 bg-black bg-opacity-80 text-white text-xs px-1 rounded">
          10:00
        </div>
      </div>

      {/* Video Info */}
      <div>
        <h3 className="font-semibold text-sm line-clamp-2">
          {content.title || 'Video Title'}
        </h3>
        <div className="flex items-center text-xs text-gray-500 mt-1 space-x-2">
          <span>100K views</span>
          <span>•</span>
          <span>1 day ago</span>
        </div>
      </div>

      {/* Channel Info */}
      <div className="flex items-center space-x-3">
        <div className="w-9 h-9 bg-red-600 rounded-full"></div>
        <div className="flex-1">
          <p className="text-sm font-semibold">Your Channel</p>
          <p className="text-xs text-gray-500">1M subscribers</p>
        </div>
        <button className="px-4 py-1.5 bg-black text-white text-sm rounded-full">
          Subscribe
        </button>
      </div>

      {/* Description */}
      <div className="text-sm text-gray-700 line-clamp-2">
        {content.description || 'Video description...'}
      </div>
    </div>
  );
}

// Google Business Preview
function GoogleBusinessPreview({ content }: { content: any }) {
  return (
    <div className="space-y-3">
      {/* Business Header */}
      <div className="flex items-center space-x-3">
        <div className="w-10 h-10 bg-blue-600 rounded"></div>
        <div>
          <p className="font-semibold text-sm">Your Business</p>
          <p className="text-xs text-gray-500">Local Business</p>
        </div>
      </div>

      {/* Post Type Badge */}
      <div className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded">
        {content.postType}
      </div>

      {/* Content Based on Type */}
      {content.postType === 'Event' && content.eventTitle && (
        <div className="border-l-4 border-blue-500 pl-3">
          <h4 className="font-semibold">{content.eventTitle}</h4>
          <p className="text-sm text-gray-600">
            📅 {content.eventDate} at {content.eventTime}
          </p>
        </div>
      )}

      {content.postType === 'Offer' && content.offerTitle && (
        <div className="bg-green-50 p-3 rounded border border-green-200">
          <h4 className="font-semibold text-green-800">🎉 {content.offerTitle}</h4>
          <p className="text-xs text-green-600 mt-1">{content.offerTerms}</p>
        </div>
      )}

      {/* Main Content */}
      <div className="text-sm text-gray-700">
        {content.text || 'Post content...'}
      </div>

      {/* CTA Button */}
      {content.callToAction && (
        <button className="px-4 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700">
          {content.callToAction.replace('_', ' ').toUpperCase()}
        </button>
      )}
    </div>
  );
}